const S3_BASE = "https://lunova.s3.us-east-1.amazonaws.com/backgrounds";
const NETWORK_TIMEOUT_MS = 4000;

export const IPAPI_KEY = "tJ9or6iZY2Yp05KCKXhiow1yYYlimxJN5VSW3yWRqrOK4gjObQ";

export const WEATHER_CODE_RULES = [
  { test: (c) => c === 0, cat: "clear", desc: "clear sky", icon: "fa-sun" },
  {
    test: (c) => c >= 1 && c <= 3,
    cat: "clouds",
    desc: "partly cloudy",
    icon: "fa-cloud-sun",
  },
  {
    test: (c) => c >= 45 && c <= 48,
    cat: "mist",
    desc: "foggy",
    icon: "fa-smog",
  },
  {
    test: (c) => c >= 51 && c <= 67,
    cat: "rain",
    desc: "rainy",
    icon: "fa-cloud-rain",
  },
  {
    test: (c) => c >= 71 && c <= 77,
    cat: "snow",
    desc: "snowy",
    icon: "fa-snowflake",
  },
  {
    test: (c) => c >= 80 && c <= 82,
    cat: "rain",
    desc: "rain showers",
    icon: "fa-cloud-showers-heavy",
  },
  {
    test: (c) => c >= 85 && c <= 86,
    cat: "snow",
    desc: "snow showers",
    icon: "fa-snowflake",
  },
  {
    test: (c) => c >= 95 && c <= 99,
    cat: "thunderstorm",
    desc: "thunderstorm",
    icon: "fa-bolt",
  },
];

export const VALID_TIME_OF_DAY = new Set([
  "morning",
  "afternoon",
  "evening",
  "night",
]);
export const VALID_WEATHER = new Set([
  "thunderstorm",
  "default",
  "clouds",
  "clear",
  "rain",
  "snow",
  "mist",
]);

export async function fetchJson(
  url,
  { fetchImpl = fetch, timeoutMs = NETWORK_TIMEOUT_MS, headers } = {}
) {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), timeoutMs);

  try {
    const res = await fetchImpl(url, { signal: ctrl.signal, headers });

    if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);

    return await res.json();
  } finally {
    clearTimeout(id);
  }
}

export const toF = (c) => Math.round((c * 9) / 5 + 32);

export async function geolocateViaIpProviders({ fetchImpl } = {}) {
  const data = await fetchJson(`https://ipapi.co/json/?key=${IPAPI_KEY}`, {
    fetchImpl,
  });

  return {
    source: "ipapi",
    city: data.city ?? null,
    latitude: data.latitude,
    longitude: data.longitude,
    countryCode: data.country_code || data.country || null,
  };
}

export async function resolveLocation(deps = {}) {
  return geolocateViaIpProviders(deps);
}

export function mapWeatherCode(code) {
  for (const rule of WEATHER_CODE_RULES) {
    if (rule.test(code))
      return { category: rule.cat, description: rule.desc, icon: rule.icon };
  }

  return { category: "default", description: "weather", icon: "fa-sun" };
}

export function makeOpenMeteoUrl(lat, lon) {
  const q = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lon),
    current_weather: "true",
  });

  return `https://api.open-meteo.com/v1/forecast?${q.toString()}`;
}

export async function fetchCurrentWeather(lat, lon, { fetchImpl } = {}) {
  const url = makeOpenMeteoUrl(lat, lon);
  const data = await fetchJson(url, { fetchImpl });
  const cw = data?.current_weather;

  if (
    !cw ||
    typeof cw.weathercode !== "number" ||
    typeof cw.temperature !== "number"
  ) {
    throw new Error("Open-Meteo response missing current_weather");
  }

  return { code: cw.weathercode, tempC: cw.temperature };
}

export function inferTimeOfDay(date = new Date()) {
  const h = date.getHours();

  if (h >= 5 && h < 12) return "morning";
  if (h >= 12 && h < 17) return "afternoon";
  if (h >= 17 && h < 21) return "evening";

  return "night";
}

export function getBackgroundUrl(timeOfDay, weather = "default") {
  const td = VALID_TIME_OF_DAY.has(timeOfDay) ? timeOfDay : "evening";
  const wt = VALID_WEATHER.has(weather) ? weather : "default";

  return `${S3_BASE}/${td}_${wt}.jpg`;
}

export async function fetchWeatherAndSetBackground(opts = {}) {
  const {
    setTemperature,
    setWeatherIcon,
    setWeatherType,
    setIsUSA,
    setBackgroundUrl,
    clock = () => new Date(),
    fetchImpl,
    geolocator,
  } = opts;

  const safeFallback = () => {
    const timeOfDay = inferTimeOfDay(clock());
    const url = getBackgroundUrl(timeOfDay, "default");

    setBackgroundUrl?.(url);

    // Set default weather data when API fails
    setTemperature?.(72);
    setWeatherIcon?.("fa-sun");
    setWeatherType?.("Clear");
    setIsUSA?.(true);

    return {
      countryCode: null,
      city: null,
      isUS: false,
      weather: {
        code: -1,
        category: "default",
        description: "weather",
        icon: "fa-sun",
        tempC: NaN,
        tempDisplay: NaN,
        unit: "°C",
      },
      timeOfDay,
      backgroundUrl: url,
    };
  };

  try {
    const loc = await resolveLocation({ fetchImpl, geolocator });
    const isUS = (loc.countryCode ?? "").toUpperCase() === "US";

    setIsUSA?.(isUS);

    const { code, tempC } = await fetchCurrentWeather(
      loc.latitude,
      loc.longitude,
      { fetchImpl }
    );
    const mapped = mapWeatherCode(code);

    const unit = isUS ? "°F" : "°C";
    const tempDisplay = isUS ? toF(tempC) : Math.round(tempC);
    const timeOfDay = inferTimeOfDay(clock());
    const backgroundUrl = getBackgroundUrl(timeOfDay, mapped.category);

    setTemperature?.(tempDisplay);
    setWeatherIcon?.(mapped.icon);
    setWeatherType?.(
      `Currently ${mapped.description}${loc.city ? ` in ${loc.city}` : ""}`
    );
    setBackgroundUrl?.(backgroundUrl);

    return {
      countryCode: loc.countryCode ?? null,
      city: loc.city ?? null,
      isUS,
      weather: {
        code,
        category: mapped.category,
        description: mapped.description,
        icon: mapped.icon,
        tempC,
        tempDisplay,
        unit,
      },
      timeOfDay,
      backgroundUrl,
    };
  } catch (error) {
    return safeFallback();
  }
}
